import { useState, useMemo, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useMaterialReactTable,
  MaterialReactTable,
} from "material-react-table";
import { Container, Button } from "react-bootstrap";
import {
  fetchPolicies,
  selectPolicies,
  selectPoliciesLoading,
  selectPoliciesError,
  selectPoliciesPagination,
  deletePolicy,
  clearError,
  importPoliciesFromClaims,
  syncClaimFormPolicies,
} from "../../redux/features/policySlice";
import PolicyForm from "./PolicyForm";
import PolicyFilters from "./PolicyFilters";
// import PolicyLocations from "./PolicyLocations";
import Loader from "../loader/Loader";
import ConfirmationModal from "../common/ConfirmationModal";
import ToastMessage from "../common/ToastMessage";
import "../common/tableStyles.css";
import { LuSearch, LuPen } from "react-icons/lu";
import { Pagination } from "@mui/material";
import { styled } from "@mui/system";
import { useStyles } from "../common/styles";
import { handleCookies } from "../../utils/cookies";
import AddressForm1 from "./AddressForm1";

function Policies() {
  const dispatch = useDispatch();
  const policies = useSelector(selectPolicies);
  const loading = useSelector(selectPoliciesLoading);
  const error = useSelector(selectPoliciesError);
  const pagination = useSelector(selectPoliciesPagination);

  const [showForm, setShowForm] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState("success");
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedFilterValue, setSelectedFilterValue] = useState("");
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [activeTab, setActiveTab] = useState("policies");
  const [isImporting, setIsImporting] = useState(false);
  const searchRef = useRef(null);
  const classes = useStyles();
  const currentUserData = handleCookies.fetchCookies();

  const StyledPagination = styled(Pagination)({
    display: "flex",
    position: "relative",
    marginTop: "-40px",
    zIndex: "999",
    justifyContent: "center",
    width: "100%",
    color: "#475467 !important",
    "& .MuiPagination-ul": {
      width: "100%",
      justifyContent: "center",
      padding: "0 16px",
    },
    "& .MuiPagination-ul li": {
      color: "#475467",
    },
    "& .MuiPagination-ul li:last-of-type, & .MuiPagination-ul li:first-of-type":
      {
        fontWeight: "600 !important",
      },
    "& .MuiPagination-ul li:last-of-type": {
      marginLeft: "auto",
    },
    "& .MuiPagination-ul li:last-of-type button::before": {
      content: "'Next'",
      marginRight: "8px",
    },
    "& .MuiPagination-ul li:first-of-type": {
      marginRight: "auto",
    },
    "& .MuiPagination-ul li:first-of-type button::after": {
      content: "'Previous'",
      marginLeft: "8px",
    },
    "& .MuiPaginationItem-root": {
      fontFamily: "Inter",
      fontSize: "14px !important",
      lineHeight: "20px",
      fontWeight: "500",
      boxShadow: "none",
    },
    "& .MuiPaginationItem-page": {
      height: "40px",
      width: "40px",
    },
    "& .MuiPaginationItem-page:hover": {
      backgroundColor: "#ECFDF3",
    },
    "& .MuiPaginationItem-page.Mui-selected": {
      backgroundColor: "#FFF",
      color: "#099250",
      border: "1px solid #CDD5DF",
      borderRadius: "8px",
    },
    "& .MuiPaginationItem-ellipsis": {
      paddingTop: "11px",
    },
  });

  useEffect(() => {
    loadPolicies(0, "", "");
  }, [dispatch]);

  const loadPolicies = (pageIndex = 0, search = "", lawFirmId = "") => {
    const offset = pageIndex * paginationState.pageSize;
    dispatch(
      fetchPolicies({
        offset,
        limit: paginationState.pageSize,
        search,
        law_firm_id: lawFirmId,
      }),
    );
  };

  const handleSearch = (value) => {
    setGlobalFilter(value);
    setPaginationState({ pageIndex: 0, pageSize: 10 });
    loadPolicies(0, value, selectedFilterValue);
  };

  const handleFilterSelect = (lawFirmId) => {
    setSelectedFilterValue(lawFirmId);
    setPaginationState({ pageIndex: 0, pageSize: 10 });
    loadPolicies(0, globalFilter, lawFirmId);
  };

  const handlePageChange = (event, page) => {
    const newPageIndex = page - 1;
    setPaginationState((prev) => ({
      ...prev,
      pageIndex: newPageIndex,
    }));
    loadPolicies(newPageIndex, globalFilter, selectedFilterValue);
  };

  const handleImportPolicies = async () => {
    setIsImporting(true);
    try {
      const result = await dispatch(importPoliciesFromClaims()).unwrap();
      await dispatch(syncClaimFormPolicies()).unwrap();

      setToastMessage(
        `Import completed! ${result.imported || 0} policies imported, ${result.skipped || 0} skipped.`,
      );
      setToastType("success");
      setShowToast(true);
      loadPolicies(
        paginationState.pageIndex,
        globalFilter,
        selectedFilterValue,
      );
    } catch (err) {
      setToastMessage(err || "Failed to import policies. Please try again.");
      setToastType("error");
      setShowToast(true);
    } finally {
      setIsImporting(false);
    }
  };

  const handleEditPolicy = (policy) => {
    setSelectedPolicy(policy);
    setShowForm(true);
  };

  const handleEditAddress = (policy) => () => {
    setSelectedPolicy(policy);
    setShowAddressModal(true);
  };

  const handleCloseAddressModal = () => {
    setSelectedPolicy(null);
    setShowAddressModal(false);
  };

  const handleSelectPolicy = (policy) => {
    setSelectedPolicy(policy);
    setActiveTab("addresses");
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedPolicy(null);
  };

  const handleFormSuccess = () => {
    handleCloseForm();
    setToastMessage("Policy updated successfully!");
    setToastType("success");
    setShowToast(true);
    loadPolicies(paginationState.pageIndex, globalFilter, selectedFilterValue);
  };

  const handleDeletePolicy = (policy) => {
    setPolicyToDelete(policy);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (policyToDelete) {
      try {
        await dispatch(deletePolicy(policyToDelete.id)).unwrap();
        setShowDeleteConfirm(false);
        setPolicyToDelete(null);
        setToastMessage("Policy deleted successfully!");
        setToastType("success");
        setShowToast(true);
        loadPolicies(
          paginationState.pageIndex,
          globalFilter,
          selectedFilterValue,
        );
      } catch (err) {
        setToastMessage("Failed to delete policy. Please try again.");
        setToastType("error");
        setShowToast(true);
      }
    }
  };

  const handleCloseError = () => {
    dispatch(clearError());
  };

  const handleCloseToast = () => {
    setShowToast(false);
  };

  const CustomTableToolbar = () => {
    return (
      <div className="d-flex justify-content-end w-100">
        <div className="table-header search-filter-gap">
          <PolicyFilters
            handleFilterSelect={handleFilterSelect}
            selectedFilterValue={selectedFilterValue}
            isAdmin={currentUserData.user_role === "Admin"}
            isTradesman={currentUserData.user_role === "Tradesman"}
            loading={loading || isImporting}
          />
        </div>
      </div>
    );
  };

  const policyColumns = useMemo(
    () => [
      {
        accessorKey: "policy_number",
        header: "Policy Number",
        size: "auto",
      },
      // {
      //   accessorKey: "policy_holder_name",
      //   header: "Policy Holder Name",
      //   size: "auto",
      // },
      // {
      //   accessorKey: "law_firm_name",
      //   header: "Law Firm",
      //   size: "auto",
      //   Cell: ({ cell, row }) => {
      //     // Display law_firm_title if available, otherwise law_firm_name
      //     return row.original.law_firm_title || cell.getValue() || "-";
      //   },
      // },
      // {
      //   accessorKey: "effective_date",
      //   header: "Effective Date",
      //   size: "auto",
      //   Cell: ({ cell }) => {
      //     const date = cell.getValue();
      //     return date ? new Date(date).toLocaleDateString() : "-";
      //   },
      // },
      // {
      //   accessorKey: "expiration_date",
      //   header: "Expiration Date",
      //   size: "auto",
      //   Cell: ({ cell }) => {
      //     const date = cell.getValue();
      //     return date ? new Date(date).toLocaleDateString() : "-";
      //   },
      // },
      {
        accessorKey: "claims_count",
        header: "Number of Claims",
        size: "auto",
        Cell: ({ cell }) => {
          const count = cell.getValue();
          return count !== null && count !== undefined ? count : "-";
        },
      },
      {
        header: "",
        accessorKey: "id",
        id: "id",
        size: 5,
        Cell: ({ cell }) => {
          const rowData = cell.row.original;
          return (
            <div className={classes.actionCell}>
              {/* <Button
                variant="link"
                size="sm"
                onClick={() => handleDeletePolicy(rowData)}
              >
                <LuTrash2 />
              </Button> */}
              <Button
                variant="link"
                className="admin-row-btn"
                size="sm"
                // onClick={() => handleEditPolicy(rowData)}
                onClick={handleEditAddress(rowData)}
              >
                <LuPen />
              </Button>
            </div>
          );
        },
      },
    ],
    [],
  );

  const customTable = useMaterialReactTable({
    data: policies,
    columns: policyColumns,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enablePagination: false,
    manualPagination: true,
    enableHiding: false,
    rowCount: pagination.total,
    enableRowSelection: false,
    enableSorting: false,
    onPaginationChange: setPaginationState,
    paginationDisplayMode: "pagination",
    state: { pagination: paginationState, globalFilter, isLoading: loading },
    getRowId: (row) => row.id,
    muiTablePaperProps: {
      sx: {
        borderRadius: "12px",
        boxShadow: "none",
        "& .MuiBox-root": {
          boxShadow: "none",
          alignItems: "center",
        },
      },
    },
    initialState: {
      showGlobalFilter: true,
    },
    muiSearchTextFieldProps: {
      placeholder: "Search",
      className: classes.searchField,
      inputProps: {
        disabled: isImporting || loading,
        sx: {
          fontFamily: "Inter",
          fontSize: "16px",
          fontWeight: "400",
          border: "none",
        },
      },
      ref: searchRef,
      sx: {
        minWidth: "252px",
        ".MuiInputBase-adornedStart": {
          padding: "0px 6px",
          background: "#fff",
        },
        button: {
          padding: "0px",
          width: "auto",
        },
        "& input": {
          padding: "8px 0",
          fontSize: "15px",
        },
        "&$focused": {
          borderColor: "#099250 !important",
          borderWidth: "1px !important",
          boxShadow: "none !important",
        },
      },
      variant: "outlined",
      InputProps: {
        startAdornment: (
          <LuSearch
            style={{
              color: "#9AA4B2",
              fontSize: "20px",
              marginRight: "10px",
            }}
          />
        ),
      },
    },
    muiTableHeadCellProps: {
      sx: {
        display: "flex",
        flex: "1 1 0",
        fontFamily: "Inter",
        textTransform: "uppercase",
        color: "#9AA4B2",
        fontSize: "10px",
        lineHeight: "19px",
        fontWeight: "700",
        padding: "6px 16px",
        borderBottom: "none",
      },
    },
    muiTableHeadRowProps: {
      sx: {
        display: "flex",
        flexDirection: "row",
        flex: "1 1 0",
        height: "52px",
        boxShadow: "none !important",
      },
    },
    muiTableBodyProps: {
      sx: {
        "& tr": {
          display: "flex",
          flex: "1 1 0",
          flexDirection: "row",
          width: "100%",
          height: "52px",
          borderRadius: "8px",
          backgroundColor: "#ffffff",
          margin: "8px 0px",
          overflow: "hidden",
          border: "1px solid #e0e0e0",
          boxShadow: "0px 1px 2px 0px #1018280F",
          boxShadow: "0px 1px 3px 0px #1018281A",
        },
        "& td": {
          flex: "1 1 0",
          display: "flex",
          alignItems: "center",
          padding: "6px 16px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          borderBottom: "none",
          fontFamily: "Inter",
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {},
    }),
    muiTopToolbarProps: {
      sx: {
        "& .MuiBox-root": {
          flexDirection: "row-reverse",
          justifyContent: "space-between",
          padding: "3px !important",
        },
        "& input: focus": {
          borderColor: "#099250 !important",
          borderWidth: "1px !important",
          boxShadow: "none !important",
        },
      },
    },
    muiPaginationProps: {
      showRowsPerPage: false,
    },
    renderTopToolbarCustomActions: () => <CustomTableToolbar />,
  });

  return (
    <>
      <Loader open={isImporting} />
      <Container>
        <div className="d-flex align-items-center justify-content-between">
          <h4 className="m-0 p-0">Policies</h4>
          {(currentUserData.user_role === "Admin" ||
            currentUserData.user_role === "Tradesman") && (
            <Button
              className="btn btn-success"
              onClick={handleImportPolicies}
              disabled={isImporting || loading}
              sx={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
            >
              {isImporting ? "Importing..." : "Import Policies from Claims"}
            </Button>
          )}
        </div>

        {error && (
          <div
            style={{
              backgroundColor: "#f8d7da",
              color: "#721c24",
              padding: "12px",
              borderRadius: "4px",
              marginTop: "16px",
              marginBottom: "16px",
            }}
          >
            {error}
            <button
              onClick={handleCloseError}
              style={{
                float: "right",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "20px",
              }}
            >
              ×
            </button>
          </div>
        )}

        {showToast && (
          <ToastMessage
            message={toastMessage}
            isVisibleToast={showToast}
            undoState={handleCloseToast}
            onUndo={handleCloseToast}
          />
        )}

        {/* <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
          style={{ marginTop: "16px" }}
        >
          <Tab eventKey="policies" title="Policies">
            
          </Tab>
          <Tab eventKey="addresses" title="Addresses">
            <div style={{ marginTop: "16px" }}>
              <PolicyLocations
                policyId={selectedPolicy?.id}
                isAllAddresses={true}
                userRole={currentUserData.user_role}
                lawFirmId={selectedFilterValue}
              />
            </div>
          </Tab>
        </Tabs> */}

        <div
          className="tab-content"
          id="policies-tab"
          style={{ marginTop: "16px" }}
        >
          <div>
            <MaterialReactTable table={customTable} />
          </div>
          {!loading && !isImporting && <StyledPagination
            page={pagination.currentPage}
            count={Math.ceil(
              (pagination.totalPages || 0),
            )}
            onChange={handlePageChange}
          />}
        </div>

        {showForm && (
          <PolicyForm
            policy={selectedPolicy}
            onClose={handleCloseForm}
            onSuccess={handleFormSuccess}
            isAdmin={currentUserData.user_role === "Admin"}
          />
        )}

        {showAddressModal && (
          <AddressForm1
            show={showAddressModal}
            onClose={handleCloseAddressModal}
            id={selectedPolicy?.id}
            policy_number={selectedPolicy?.policy_number}
            claims={selectedPolicy?.claim_numbers}
            effective_date={selectedPolicy?.effective_date}
          />
        )}

        <ConfirmationModal
          show={showDeleteConfirm}
          onHide={() => setShowDeleteConfirm(false)}
          heading="Delete Policy"
          warningText={`Are you sure you want to delete policy "${policyToDelete?.policy_number}"? This action cannot be undone.`}
          text="Delete"
          onAction={handleConfirmDelete}
          isWarningImage={true}
          bgColor="#dc3545"
        />
      </Container>
    </>
  );
}

export default Policies;
